/*
 * product-media-gallery-v3.js
 *
 * Custom element backing snippets/product-media-gallery-v3.liquid.
 *
 * Single-layout variant of v2: one Swiper carousel with a thumbnail bar below it,
 * rendered at all breakpoints. The v2 desktop hero grid is removed entirely.
 *
 * Responsibilities:
 *   1. Filename-based image filter (ported from docs/theme_function/PRODUCT_MEDIA_FILTER.md). When a variant is
 *      selected, images whose filenames match the variant SKU / base SKU / general keywords
 *      stay visible; everything else is hidden via display:none.
 *   2. Swiper carousel + thumbnail bar — same Swiper init as v2's mobile path, but the
 *      slider is rebuilt on every filter pass so hidden slides don't leave blank gaps.
 *   3. PhotoSwipe zoom — single lightbox instance, rebuilt with visible items on every filter
 *      pass. Click delegate is bound once to avoid stale closures across re-inits.
 *
 * Public contract (called by assets/product-info.js):
 *   element.init()                 — idempotent bootstrap
 *   element.setActiveMedia(variant) — runs the filter pipeline for the given Shopify variant
 */

if (!customElements.get('product-media-gallery-v3')) {
  // Display-order constants (CSS order values; lower paints first). Ported
  // from lac-theme product-image-filter.js — canonical 7-slot order with the
  // legacy families slotted in (35 = legacy hero shots; 55 = legacy "main"
  // group/range shots, demoted to sit just behind pkg/alternates so a product
  // line's group shot — e.g. CCL701-707_main — never leads the variant's own
  // hero/swatch/alternate shots):
  //   10 1 — glam             *_glam
  //   20 2 — front_closed     *_front_closed
  //   30 3 — front_open       *_front_open
  //   35 primary (legacy)     *_prod_img(_01) / name-prefix-SKU-01 / SKU_01 /
  //                           unknown SKU-matched suffix
  //   40 4 — swatch           *_prod_sw / *_swatch / *_product_sw / *_sw
  //   50 5 — pkg/alternates   *_pkg_front/back / *altimg* / *altlmg* /
  //                           *_alt_img# / *_prod_alt* / numbered 02+ /
  //                           shared & no-SKU images
  //   55 main (legacy group)  *_prod_img_main / *_main / SKU-range_main_#
  //   60 6 — infographics     *infograph*
  //   65   — model            *model*  (behind infographics, ahead of lifestyle)
  //   70 7 — lifestyle        *lifestyle*
  const ORDER_MAIN = '55';
  const ORDER_GLAM = '10';
  const ORDER_FRONT_CLOSED = '20';
  const ORDER_FRONT_OPEN = '30';
  const ORDER_PRIMARY = '35';
  const ORDER_SWATCH = '40';
  const ORDER_ALT = '50';
  const ORDER_INFOGRAPH = '60';
  const ORDER_MODEL = '65';
  const ORDER_LIFESTYLE = '70';
  // Variant-vs-general band (ported from v1's "variant images first" rule): a
  // general/product-level image sorts into the SAME 7 families as variant
  // images, but always lands behind every variant image. Adding this offset to
  // a general image's family order maps 10→110, 50→150, 70→170 — so variant
  // shots lead while family order is preserved within each band.
  const ORDER_GENERAL_OFFSET = 100;
  // Shopify duplicate-upload UUID suffix (e.g. _a4305b68_0588_4f2b_…) — stripped
  // before SKU extraction so it doesn't parse as a fake SKU token.
  const UUID_REGEX = /_?[0-9A-F]{8}_[0-9A-F]{4}_[0-9A-F]{4}_[0-9A-F]{4}_[0-9A-F]{12}/g;

  customElements.define(
    'product-media-gallery-v3',
    class ProductMediaGalleryV3 extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          mediaItems: '.product__media-item-v2--mobile',
          thumbItems: '.product__thumbs-item-v2',
          viewer: '[id^="GalleryViewer-"]',
          thumbnails: '[id^="GalleryThumbnails-"]',
          sliderWrapper: '[id^="Slider-Gallery-"]',
          thumbsWrapper: '[id^="Slider-Thumbnails-"]',
        };

        this.initialized = false;
        this.sliderInstance = null;
        this.thumbsInstance = null;
        this.lightbox = null;
        this.currentDataSource = [];
        this.lastVariantSku = null;
        this.imageFilterCache = new Map();
        this.productSkuCache = null;
      }

      // ----------------------------------------------------------------------
      //  Filter config — ported verbatim from docs/theme_function/PRODUCT_MEDIA_FILTER.md.
      //  `generalKeywords` starts empty; populate per-store as filename conventions solidify.
      // ----------------------------------------------------------------------

      static get FILTER_CONFIG() {
        return {
          minVariantImages: 3,
          minThumbnailVariants: 2,
          generalKeywords: [
            'share',
            'shared',
            'common',
            'video',
            'general',
            'lifestyle',
            'lifestyles',
            'videos',
            'public',
            'main',
            'infographics',
            'model',
            'logo',
            'display',
            'stories',
            'gifs',
            'product_announcements',
            'infograph',
          ],
          // PROD_ALTIMG intentionally NOT excluded for LAC — the prod_altimg
          // family is 380+ legitimate alternate shots here (they land in the
          // alternates slot via getImageOrder, not hidden).
          excludedPatterns: ['TEMP', 'DRAFT', 'BACKUP'],
        };
      }

      static get ENHANCED_SKU_REGEX() {
        return /[A-Za-z]+\d+[A-Za-z0-9_-]*/gi;
      }

      static get EXCLUDED_PATTERNS_REGEX() {
        return /TEMP|DRAFT|BACKUP/i;
      }

      static get GENERAL_IMAGE_KEYWORDS() {
        return ProductMediaGalleryV3.FILTER_CONFIG.generalKeywords.map((k) =>
          k.toUpperCase().replace(/[_\-\s.]/g, '')
        );
      }

      // ----------------------------------------------------------------------
      //  Lifecycle
      // ----------------------------------------------------------------------

      connectedCallback() {
        // Init synchronously the moment the Custom Element upgrades. The
        // gallery is above-the-fold on PDP — there's no benefit to deferring
        // via Motion.inView (IntersectionObserver), and the async hop the
        // observer adds is exactly the window where the browser paints the
        // unfiltered DOM. With the `gallery-loading` CSS class set in Liquid,
        // slides are visibility:hidden until init() completes, so even if a
        // paint slips between HTML parse and this script's defer execution,
        // the user never sees the unfiltered state.
        this.init();
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;

        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        // Per-product opt-in: when the `custom.manual_media_order` metafield is
        // on, the gallery follows Shopify's Media-section order instead of the
        // filename 7-slot sort. Default off (absent metafield → filename sort).
        this.manualMediaOrder = this.dataset.manualMediaOrder === 'true';

        this.mediaItems = Array.from(this.querySelectorAll(this.selectors.mediaItems));
        this.thumbItems = Array.from(this.querySelectorAll(this.selectors.thumbItems));

        this.viewerEl = this.querySelector(this.selectors.viewer);
        this.thumbnailsEl = this.querySelector(this.selectors.thumbnails);
        this.sliderWrapperEl = this.querySelector(this.selectors.sliderWrapper);
        this.thumbsWrapperEl = this.querySelector(this.selectors.thumbsWrapper);

        // Pre-filter the DOM with the selected-variant SKU before Swiper sees
        // it, so slide 0 is the correct image from the very first paint.
        // Without this, Swiper inits with the unfiltered server-rendered order,
        // shows whatever Shopify's natural product.media[0] is, then jumps to
        // the post-filter slide 0 once setActiveMedia() runs from product-info.js
        // — visible as a flash/jump of the main image on PDP entry.
        const initialSku = (this.dataset.initialVariantSku || '').trim();
        if (initialSku) {
          this.filterList(this.mediaItems, initialSku);
          this.filterList(this.thumbItems, initialSku);
          this.imageFilterCache.set(`slider_${initialSku}`, { timestamp: Date.now() });
          this.lastVariantSku = initialSku;

          // Physically reorder the Swiper wrappers' children so Swiper's slide
          // index 0 maps to the first filtered-visible item. Setting CSS
          // `order` alone isn't enough — Swiper iterates DOM children, not CSS
          // visual order. This is the same reorder rebuildSwiperSlides() does
          // post-init; we run it pre-init so the very first Swiper render is
          // already correct.
          this.reorderWrapperPreInit(this.sliderWrapperEl, this.mediaItems);
          this.reorderWrapperPreInit(this.thumbsWrapperEl, this.thumbItems);
        }

        this.initSlider();

        if (this.enableImageZoom) {
          this.bindZoomDelegate();
          this.initImageZoom();
        }

        // Reveal the gallery now that filter + Swiper are both ready.
        // The `gallery-loading` class set by Liquid kept slides
        // visibility:hidden up to this moment, so the very first paint the
        // user sees has the post-filter slide 0 — no flash, no jump.
        this.classList.remove('gallery-loading');
        this.classList.add('gallery-ready');
      }

      // Pre-init DOM reorder helper. Same sort logic as rebuildSwiperSlides()
      // but doesn't require an existing Swiper instance — used during initial
      // setup before the slider is constructed.
      reorderWrapperPreInit(wrapper, allItems) {
        if (!wrapper || !allItems || allItems.length === 0) return;

        const visibleOrdered = allItems
          .map((item, domIdx) => ({
            item,
            order: parseInt(item.style.order || '0', 10) || 0,
            domIdx,
          }))
          .filter(({ item }) => !item.classList.contains('filtered-out'))
          .sort((a, b) => a.order - b.order || a.domIdx - b.domIdx)
          .map(({ item }) => item);

        while (wrapper.firstChild) {
          wrapper.removeChild(wrapper.firstChild);
        }

        visibleOrdered.forEach((item) => {
          item.style.display = '';
          item.style.visibility = '';
          wrapper.appendChild(item);
        });
      }

      // ----------------------------------------------------------------------
      //  Filter logic — ported from docs/theme_function/PRODUCT_MEDIA_FILTER.md
      // ----------------------------------------------------------------------

      getBaseSku(variantSku) {
        if (!variantSku) return null;
        const match = variantSku.match(/^([A-Za-z]+\d+)(?=[A-Za-z]+$)/);
        return match ? match[1] : null;
      }

      hasBaseSkuPrefix(fileName, baseSku) {
        if (!baseSku || !fileName.startsWith(baseSku)) return false;
        const nextChar = fileName.charAt(baseSku.length);
        return nextChar === '' || ['_', '-', '.', ' '].includes(nextChar);
      }

      extractSkuCandidates(fileName) {
        const matches = fileName.match(ProductMediaGalleryV3.ENHANCED_SKU_REGEX) || [];
        const candidates = [];
        matches.forEach((m) => {
          const subParts = m.match(/[A-Za-z]+\d+/gi) || [];
          subParts.forEach((s) => candidates.push(s.toUpperCase()));
        });
        return candidates;
      }

      // Uppercase, separators -> "_", Shopify duplicate-upload UUID suffix removed.
      flatten(rawName) {
        return rawName
          .toUpperCase()
          .replace(/[-\s.]/g, '_')
          .replace(UUID_REGEX, '');
      }

      // SKU range convention: "CLG461-466_..." covers CLG461..CLG466
      matchesSkuRange(flat, normalizedSku) {
        const m = normalizedSku.match(/^([A-Z]+)(\d+)/);
        if (!m) return false;
        const skuLetters = m[1];
        const skuNum = parseInt(m[2], 10);
        const rangeRe = /([A-Z]+)(\d+)_(\d{2,})/g;
        let r;
        while ((r = rangeRe.exec(flat)) !== null) {
          if (r[1] !== skuLetters) continue;
          const start = parseInt(r[2], 10);
          const end = parseInt(r[3], 10);
          if (end > start && skuNum >= start && skuNum <= end) return true;
        }
        return false;
      }

      // Wildcard convention: "CLE82X_...", "CLIPC8xx_..." covers all shades
      // sharing the prefix before the X(s)
      matchesWildcardSku(flat, normalizedSku) {
        const wildRe = /([A-Z]+\d+)X{1,2}(?=_|$)/g;
        let m;
        while ((m = wildRe.exec(flat)) !== null) {
          if (normalizedSku.indexOf(m[1]) === 0) return true;
        }
        return false;
      }

      // SKUs (+ base SKUs + shade-number digits) of this product's variants,
      // read from data-product-variant-skus on the gallery root. Used to tell a
      // sibling variant's keyword image (hide) from a legacy-SKU keyword image
      // matching no variant at all (show as general).
      getProductSkuSets() {
        if (this.productSkuCache) return this.productSkuCache;
        const skus = {};
        const bases = {};
        const digits = {};
        (this.dataset.productVariantSkus || '')
          .split(',')
          .map((s) => s.toUpperCase().trim())
          .filter(Boolean)
          .forEach((s) => {
            skus[s] = true;
            const b = this.getBaseSku(s);
            if (b) bases[b] = true;
            const d = (s.match(/\d{2,}/) || [null])[0];
            if (d) digits[d] = true;
          });
        this.productSkuCache = { skus, bases, digits };
        return this.productSkuCache;
      }

      isGeneralImageName(flat) {
        if (ProductMediaGalleryV3.EXCLUDED_PATTERNS_REGEX.test(flat)) return false;
        const stripped = flat.replace(/_/g, '');
        return ProductMediaGalleryV3.GENERAL_IMAGE_KEYWORDS.some((k) => stripped.includes(k));
      }

      // ----------------------------------------------------------------------
      //  Display order (the 7-slot spec) — ported from lac-theme
      // ----------------------------------------------------------------------

      getImageOrder(flat, isVariantImage) {
        const order = this.getFamilyOrder(flat, isVariantImage);
        // Two-level sort that combines v1 + the 7-slot port:
        //   (1) every variant image leads every general/product-level image, then
        //   (2) the 7-slot family order applies within each band.
        // Variant images keep their natural slot (5..70); general images sort
        // into the SAME families but offset into a band behind every variant
        // image (105..170) — so e.g. a general infographic (160) never jumps
        // ahead of the variant's own lifestyle shot (70).
        if (!isVariantImage) {
          return String(parseInt(order, 10) + ORDER_GENERAL_OFFSET);
        }
        return order;
      }

      getFamilyOrder(flat, isVariantImage) {
        if (flat.indexOf('LIFESTYLE') !== -1) return ORDER_LIFESTYLE;
        if (flat.indexOf('INFOGRAPH') !== -1) return ORDER_INFOGRAPH;
        // "model" keyword shots sort directly behind infographics (ahead of
        // lifestyle). Checked before the swatch/front/alt branches so a model
        // filename's slot is authoritative regardless of other tokens.
        if (flat.indexOf('MODEL') !== -1) return ORDER_MODEL;

        if (/(_PROD_SW|_PRODUCT_SW|SWATCH)(_\d+)?(_|$)/.test(flat) || /_SW(_\d+)?$/.test(flat)) {
          return ORDER_SWATCH;
        }

        if (flat.indexOf('FRONT_CLOSED') !== -1) return ORDER_FRONT_CLOSED;

        if (/PKG_FRONT|PKG_BACK|ALTLMG|ALTIMG|ALT_IMG|PROD_ALT/.test(flat)) {
          return ORDER_ALT;
        }
        if (/_ALT_?\d+$/.test(flat)) return ORDER_ALT;

        // Shared/common images are product-level content — alternates slot per
        // spec, even when range/wildcard-matched to the current variant.
        if (/SHARED|_SHARE(_|$)|COMMON/.test(flat)) return ORDER_ALT;

        if (flat.indexOf('MAIN') !== -1) return ORDER_MAIN;
        if (flat.indexOf('FRONT_OPEN') !== -1) return ORDER_FRONT_OPEN;
        if (flat.indexOf('GLAM') !== -1) return ORDER_GLAM;

        const numbered = flat.match(/_PROD_IMG_(\d{1,2})$|_PROD_IMAGE_(\d{1,2})$/);
        if (numbered) {
          const n = parseInt(numbered[1] || numbered[2], 10);
          return n <= 1 ? ORDER_PRIMARY : ORDER_ALT;
        }
        if (/PROD_IMG|PROD_IMAGE/.test(flat)) return ORDER_PRIMARY;

        // Trailing shot number: SKU_01 / aura-halo-gel-cnl767-02 (flattened)
        const trailing = flat.match(/_(\d{1,2})$/);
        if (trailing) {
          return parseInt(trailing[1], 10) <= 1 ? ORDER_PRIMARY : ORDER_ALT;
        }

        // Unknown suffix: variant-matched images lead (older products' primary
        // shots, e.g. CP685_WISTERIA); product-level images go to alternates.
        return isVariantImage ? ORDER_PRIMARY : ORDER_ALT;
      }

      classifyImageName(rawName, variantSku) {
        if (!rawName) return { show: false, type: 'hidden', order: null };

        const flat = this.flatten(rawName);
        const fileName = rawName.toUpperCase();
        const normalizedSku = (variantSku || '').toUpperCase().trim();
        const baseSku = this.getBaseSku(normalizedSku);

        if (
          normalizedSku &&
          (fileName.startsWith(normalizedSku) || this.hasBaseSkuPrefix(fileName, baseSku))
        ) {
          return { show: true, type: 'variant', order: this.getImageOrder(flat, true) };
        }

        // Extract candidates from the UUID-stripped name — Shopify's duplicate-
        // upload hex suffixes otherwise parse as fake SKU tokens.
        const skuCandidates = this.extractSkuCandidates(flat);
        const containsCurrentSku = skuCandidates.some(
          (c) => c === normalizedSku || (baseSku && c === baseSku)
        );
        if (containsCurrentSku) {
          return { show: true, type: 'variant', order: this.getImageOrder(flat, true) };
        }

        if (normalizedSku && this.matchesSkuRange(flat, normalizedSku)) {
          return { show: true, type: 'variant', order: this.getImageOrder(flat, true) };
        }
        if (normalizedSku && this.matchesWildcardSku(flat, normalizedSku)) {
          return { show: true, type: 'variant', order: this.getImageOrder(flat, true) };
        }

        if (skuCandidates.length > 0) {
          const sets = this.getProductSkuSets();
          const skuDigits = (normalizedSku.match(/\d{2,}/) || [null])[0];

          // Legacy-renamed family: filename keeps an old letter prefix but the
          // shade number still maps to the selected variant (GLC601_* on CLS601,
          // CMS316A_* on CBMS316).
          if (skuDigits) {
            const digitsMatchCurrent = skuCandidates.some(
              (c) => (c.match(/\d{2,}/) || [null])[0] === skuDigits
            );
            if (digitsMatchCurrent) {
              return { show: true, type: 'variant', order: this.getImageOrder(flat, true) };
            }
          }

          // Keyword image whose SKU maps to NO variant of this product (by prefix
          // OR shade number) is product-level legacy content: show as general. A
          // sibling variant's keyword image (same shade number, any prefix) hides.
          if (this.isGeneralImageName(flat)) {
            const belongsToSibling = skuCandidates.some((c) => {
              if (sets.skus[c] || sets.bases[c]) return true;
              const d = (c.match(/\d{2,}/) || [null])[0];
              return !!(d && sets.digits[d]);
            });
            if (!belongsToSibling) {
              return { show: true, type: 'general', order: this.getImageOrder(flat, false) };
            }
          }
          // SKU-named image that doesn't match the selected variant: hide
          // (sibling or legacy/discontinued SKU — variant selection wins).
          // If a variant ends up with zero matches, the safety net shows all.
          return { show: false, type: 'other-variant', order: null };
        }

        if (this.isGeneralImageName(flat)) {
          return { show: true, type: 'general', order: this.getImageOrder(flat, false) };
        }

        // No SKU token, no keyword: keep it visible for every variant.
        return { show: true, type: 'general', order: this.getImageOrder(flat, false) };
      }

      applyDisplayChange(element, show) {
        if (show) {
          element.style.display = '';
          element.style.visibility = 'visible';
          element.classList.remove('filtered-out');
        } else {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.classList.add('filtered-out');
        }
        this.forceStyleRecalculation(element);
      }

      forceStyleRecalculation(element) {
        return element.offsetHeight;
      }

      filterList(items, variantSku) {
        let visibleProductImages = 0;
        let visibleGeneralImages = 0;

        items.forEach((item) => {
          const rawName = item.dataset.name || '';
          const decision = this.classifyImageName(rawName, variantSku);

          if (decision.show) {
            this.applyDisplayChange(item, true);
            if (decision.order) item.style.order = decision.order;
            if (decision.type === 'variant') visibleProductImages++;
            else if (decision.type === 'general') visibleGeneralImages++;
          } else {
            this.applyDisplayChange(item, false);
            item.style.order = '';
          }
        });

        const threshold = Math.min(
          ProductMediaGalleryV3.FILTER_CONFIG.minVariantImages,
          items.length
        );

        if (visibleProductImages < threshold && variantSku) {
          const normalizedSku = variantSku.toUpperCase().trim();
          const baseSku = this.getBaseSku(normalizedSku);

          items.forEach((item) => {
            if (!item.classList.contains('filtered-out')) return;
            const rawName = item.dataset.name || '';
            const fileName = rawName.toUpperCase();
            const looseMatch =
              (normalizedSku && fileName.includes(normalizedSku)) ||
              (baseSku && fileName.includes(baseSku));
            if (looseMatch) {
              this.applyDisplayChange(item, true);
              item.style.order = this.getImageOrder(this.flatten(rawName), true);
              visibleProductImages++;
            }
          });
        }

        // Safety net: never leave the gallery empty (e.g. variant SKU/filename
        // data mismatch). Show everything rather than render a blank gallery.
        if (visibleProductImages + visibleGeneralImages === 0) {
          items.forEach((item) => {
            this.applyDisplayChange(item, true);
            item.style.order = '';
          });
        }

        // Manual media order (per-product `custom.manual_media_order` metafield):
        // filtering above still decides what's visible; when the toggle is on we
        // discard the filename 7-slot order and lay the visible items out in
        // Shopify's Media-section order (data-media-index on media items,
        // data-slide-index on thumbnails — both 0-based product.media positions).
        if (this.manualMediaOrder) {
          items.forEach((item) => {
            if (item.classList.contains('filtered-out')) return;
            const pos = item.dataset.mediaIndex ?? item.dataset.slideIndex ?? '0';
            item.style.order = String(parseInt(pos, 10) || 0);
          });
        }

        return { visibleProductImages, visibleGeneralImages };
      }

      filterMedia(variantSku) {
        const cacheKey = `slider_${variantSku || ''}`;
        const cached = this.imageFilterCache.get(cacheKey);
        const shouldUseCache = cached && this.lastVariantSku === variantSku;

        if (!shouldUseCache) {
          this.filterList(this.mediaItems, variantSku);
          this.filterList(this.thumbItems, variantSku);

          this.imageFilterCache.set(cacheKey, {
            timestamp: Date.now(),
          });
          this.trimCache(15);
        }

        this.lastVariantSku = variantSku;
        this.refreshSlider();

        // Intentionally NOT replaying motion-element animations here. v2's
        // behavior is preserved: animation plays exactly once per motion-element
        // lifetime (when the image first enters the viewport and has loaded),
        // and NEVER again on variant switch. See v2 file for the full rationale.

        if (this.enableImageZoom) {
          this.initImageZoom();
        }
      }

      trimCache(max) {
        if (this.imageFilterCache.size <= max) return;
        const excess = this.imageFilterCache.size - max;
        const keys = Array.from(this.imageFilterCache.keys());
        for (let i = 0; i < excess; i++) {
          this.imageFilterCache.delete(keys[i]);
        }
      }

      // ----------------------------------------------------------------------
      //  Swiper — ported from v2's mobile path, simplified to run at all
      //  breakpoints (no matchMedia gating, no destroy/init on resize).
      // ----------------------------------------------------------------------

      initSlider() {
        if (this.sliderInstance) return;
        if (!this.viewerEl || !window.FoxTheme || !window.FoxTheme.Carousel) return;

        const mediaItemGap = parseInt(this.dataset.mediaItemGap, 10) || 10;

        if (this.thumbnailsEl) {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.thumbnailsEl, {
            slidesPerView: 4,
            breakpoints: {
              640: { slidesPerView: 5 },
              1024: { slidesPerView: 6 },
            },
            spaceBetween: mediaItemGap,
            loop: false,
            freeMode: true,
            watchSlidesProgress: true,
            threshold: 2,
          });
          this.thumbsInstance.init();
          this.bindEdgeFade();
        }

        const sliderOptions = {
          init: false,
          slidesPerView: 1,
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          threshold: 2,
          speed: 0,
        };

        if (this.thumbsInstance && FoxTheme.Swiper && FoxTheme.Swiper.Thumbs) {
          sliderOptions.thumbs = {
            swiper: this.thumbsInstance.slider,
            autoScrollOffset: 2,
          };
        }

        const modules = FoxTheme.Swiper && FoxTheme.Swiper.Thumbs ? [FoxTheme.Swiper.Thumbs] : [];
        this.sliderInstance = new window.FoxTheme.Carousel(this.viewerEl, sliderOptions, modules);
        this.sliderInstance.init();
        if (this.sliderInstance.slider && typeof this.sliderInstance.slider.init === 'function') {
          this.sliderInstance.slider.init();
        }

        if (this.sliderInstance.slider) {
          this.sliderInstance.slider.on('realIndexChange', (swiper) => {
            const { slides, activeIndex, thumbs } = swiper;
            if (thumbs && thumbs.swiper) thumbs.swiper.slideTo(activeIndex);
            if (slides && slides[activeIndex]) this.playActiveMedia(slides[activeIndex]);
            this.syncThumbActiveState();
          });
        }

        this.syncThumbActiveState();
      }

      refreshSlider() {
        if (!this.sliderInstance || !this.sliderInstance.slider) return;

        // Swiper doesn't reliably handle display:none slides — clicking a thumbnail
        // would translate the slider to a phantom hidden slot, leaving the viewport
        // blank. Instead of hiding, we physically detach all slides from the wrapper
        // and re-append only the visible ones in visual order (CSS order asc,
        // DOM index as tiebreaker). Applied identically to the main slider and the
        // thumb bar so index-based Thumbs-module sync stays 1:1 between them.
        this.rebuildSwiperSlides(this.sliderInstance.slider, this.mediaItems);
        if (this.thumbsInstance && this.thumbsInstance.slider) {
          this.rebuildSwiperSlides(this.thumbsInstance.slider, this.thumbItems);
        }

        try {
          this.sliderInstance.slider.slideTo(0, 0, false);
        } catch (e) {}
        if (this.thumbsInstance && this.thumbsInstance.slider) {
          try {
            this.thumbsInstance.slider.slideTo(0, 0, false);
          } catch (e) {}
        }

        this.syncThumbActiveState();
        this.updateEdgeFade();
      }

      // ----------------------------------------------------------------------
      //  Edge-fade hint — opt-in via section setting `thumbnail_edge_fade`
      //  (data-edge-fade="true"). Toggles `is-fade-left` / `is-fade-right` on
      //  the thumbnails container so CSS pseudo-elements paint a gradient over
      //  the outer half of the first/last visible thumb when more thumbs sit
      //  offscreen. Hides the fade at the corresponding scroll edge.
      //
      //  Swiper's `progress` event is the cleanest hook for free-mode thumb
      //  carousels: progress = 0 → at start; progress = 1 → at end. We also
      //  bind a native scroll listener on the wrapper as a defensive fallback,
      //  since some Swiper builds don't emit `progress` reliably during
      //  pointer drags on free-mode bars.
      // ----------------------------------------------------------------------

      bindEdgeFade() {
        if (this.dataset.edgeFade !== 'true') return;
        if (!this.thumbnailsEl || !this.thumbsInstance || !this.thumbsInstance.slider) return;

        const swiper = this.thumbsInstance.slider;
        const update = () => this.updateEdgeFade();

        swiper.on('progress', update);
        swiper.on('setTranslate', update);
        swiper.on('resize', update);
        swiper.on('update', update);

        // Defensive: native scroll on the underlying wrapper covers any
        // pointer-drag path that Swiper's progress event doesn't catch.
        if (this.thumbsWrapperEl) {
          this.thumbsWrapperEl.addEventListener('scroll', update, { passive: true });
        }

        // Initial paint after layout settles.
        requestAnimationFrame(update);
      }

      updateEdgeFade() {
        if (this.dataset.edgeFade !== 'true') return;
        if (!this.thumbnailsEl || !this.thumbsInstance || !this.thumbsInstance.slider) return;

        const swiper = this.thumbsInstance.slider;
        const epsilon = 0.01;

        // Swiper exposes `progress` (0..1) and `isBeginning` / `isEnd` flags.
        // Fall back to translate vs maxTranslate if progress is undefined
        // (very early in init, before any slides have rendered).
        let progress = typeof swiper.progress === 'number' ? swiper.progress : 0;
        if (Number.isNaN(progress)) progress = 0;

        // If the thumb bar isn't actually overflowing, hide both fades.
        // Detect via `isLocked` (Swiper sets this when content fits the viewport)
        // or by comparing the underlying wrapper's scrollable width.
        const wrapper = this.thumbsWrapperEl || (swiper.wrapperEl || null);
        const overflowsByDom = wrapper
          ? wrapper.scrollWidth - wrapper.clientWidth > 1
          : true;
        const overflows = !swiper.isLocked && overflowsByDom;

        const showLeft = overflows && progress > epsilon;
        const showRight = overflows && progress < 1 - epsilon;

        this.thumbnailsEl.classList.toggle('is-fade-left', showLeft);
        this.thumbnailsEl.classList.toggle('is-fade-right', showRight);
      }

      syncThumbActiveState() {
        if (!this.thumbsInstance || !this.thumbsInstance.slider) return;
        if (!this.sliderInstance || !this.sliderInstance.slider) return;

        const main = this.sliderInstance.slider;
        const thumbs = this.thumbsInstance.slider;
        const activeIdx = typeof main.activeIndex === 'number' ? main.activeIndex : 0;

        if (this.thumbItems && this.thumbItems.length > 0) {
          this.thumbItems.forEach((slide) => {
            if (slide && slide.classList) {
              slide.classList.remove('swiper-slide-thumb-active');
            }
          });
        }

        const currentSlides = thumbs.slides;
        if (!currentSlides || currentSlides.length === 0) return;
        const targetThumb = currentSlides[activeIdx];
        if (targetThumb && targetThumb.classList) {
          targetThumb.classList.add('swiper-slide-thumb-active');
        }

        try {
          thumbs.slideTo(activeIdx, 0, false);
        } catch (e) {}
      }

      rebuildSwiperSlides(swiper, allItems) {
        if (!swiper || !swiper.wrapperEl || !allItems || allItems.length === 0) return;

        const wrapper = swiper.wrapperEl;

        const visibleOrdered = allItems
          .map((item, domIdx) => ({
            item,
            order: parseInt(item.style.order || '0', 10) || 0,
            domIdx,
          }))
          .filter(({ item }) => !item.classList.contains('filtered-out'))
          .sort((a, b) => a.order - b.order || a.domIdx - b.domIdx)
          .map(({ item }) => item);

        while (wrapper.firstChild) {
          wrapper.removeChild(wrapper.firstChild);
        }

        visibleOrdered.forEach((item) => {
          item.style.display = '';
          item.style.visibility = '';
          wrapper.appendChild(item);
        });

        try {
          swiper.update();
        } catch (e) {}
      }

      playActiveMedia(slide) {
        const deferredMedia = slide.querySelector('product-model');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false);
        }
      }

      // ----------------------------------------------------------------------
      //  PhotoSwipe zoom — ported from v2.
      // ----------------------------------------------------------------------

      bindZoomDelegate() {
        if (this.zoomDelegateBound) return;
        this.zoomDelegateBound = true;

        if (!window.FoxTheme || !window.FoxTheme.utils || !window.FoxTheme.utils.addEventDelegate) return;

        window.FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom-v2',
          context: this,
          handler: (e, button) => {
            if (!button || button.dataset.mediaType !== 'image') return;
            if (!this.lightbox || !this.currentDataSource.length) return;
            const mediaId = button.dataset.mediaId;
            const idx = this.currentDataSource.findIndex((d) => d.domEl.dataset.mediaId === mediaId);
            if (idx >= 0) this.lightbox.loadAndOpen(idx);
          },
        });
      }

      initImageZoom() {
        if (!window.FoxTheme || !window.FoxTheme.PhotoSwipeLightbox) return;

        const visibleItems = this.mediaItems.filter((item) => !item.classList.contains('filtered-out'));

        if (this.lightbox) {
          try {
            this.lightbox.destroy();
          } catch (e) {}
          this.lightbox = null;
        }

        this.currentDataSource = visibleItems
          .map((media, idx) => {
            if (media.dataset.mediaType !== 'image') return null;
            const width = parseInt(media.dataset.pswpWidth, 10);
            const height = parseInt(media.dataset.pswpHeight, 10);
            if (!width || !height) return null;
            return {
              id: idx,
              src: media.dataset.src,
              width,
              height,
              mediaType: 'image',
              domEl: media,
            };
          })
          .filter(Boolean);

        if (this.currentDataSource.length === 0) return;

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: this.currentDataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          preload: [1, 4],
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }) => {
          const entry = this.currentDataSource[id];
          if (entry && entry.domEl) {
            const img = entry.domEl.querySelector('img');
            if (img) return img;
          }
          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data && data.src) return data.src;
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          if (window.pauseAllMedia) window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          if (this.sliderInstance && this.sliderInstance.slider && this.lightbox && this.lightbox.pswp) {
            try {
              this.sliderInstance.slider.slideToLoop(this.lightbox.pswp.currIndex, 0, false);
            } catch (e) {}
          }
        });

        this.lightbox.on('uiRegister', () => {
          // Liquid-glass wrapper — the 4 vb-glass layers + the icon in vb-glass__content.
          // .vb-glass + .vb-glass--lift give the frosted look, #386B78 icon, and
          // the hover shadow/tint (see snippets/liquid-glass-defs.liquid).
          const glassBtn = (svg) =>
            '<span class="vb-glass__filter"></span><span class="vb-glass__overlay"></span><span class="vb-glass__specular"></span><span class="vb-glass__content">' +
            svg +
            '</span>';
          this.lightbox.pswp.ui.registerElement({
            name: 'next',
            ariaLabel: 'Next slide',
            order: 3,
            isButton: true,
            className: 'vb-glass vb-glass--lift',
            html: glassBtn('<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>'),
            onClick: () => this.lightbox.pswp.next(),
          });
          this.lightbox.pswp.ui.registerElement({
            name: 'prev',
            ariaLabel: 'Previous slide',
            order: 1,
            isButton: true,
            className: 'vb-glass vb-glass--lift',
            html: glassBtn('<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>'),
            onClick: () => this.lightbox.pswp.prev(),
          });
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            className: 'vb-glass vb-glass--lift',
            html: glassBtn('<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>'),
            onClick: () => this.lightbox.pswp.close(),
          });
        });

        this.lightbox.init();
      }

      // ----------------------------------------------------------------------
      //  Public API — called by assets/product-info.js
      // ----------------------------------------------------------------------

      setActiveMedia(variant) {
        if (!variant) return;
        const sku = variant.sku || '';
        this.filterMedia(sku);
      }
    }
  );
}
